import { IDynamicFormFieldsJson } from "../../models/user/DynamicForm/DynamicForm";
import {
  loadAddDriverFormSchema,
  loadAddVehicleFormSchema,
} from "./voiceFormLoaders";

/** How the voice UI renders this form. */
export type VoiceFormRendererKind = "builtin_claim" | "dynamic";

export type VoiceFormIntentId = string;

export interface VoiceFormPolicyContext {
  policyId: string;
  carrierPolicyNo: string;
  lobCode: string;
  carrier: string;
  system: string;
  policyType?: string;
}

export interface VoiceFormLoadContext {
  policy?: VoiceFormPolicyContext;
}

export interface VoiceFormDefinition {
  id: VoiceFormIntentId;
  title: string;
  description: string;
  /** Spoken or typed phrases that select this form. */
  intentPatterns: RegExp[];
  renderer: VoiceFormRendererKind;
  requiresPolicy: boolean;
  /** Only used when renderer is "dynamic". */
  loadForm?: (ctx: VoiceFormLoadContext) => Promise<IDynamicFormFieldsJson | null>;
}

/**
 * Register voice-enabled forms here. Add entries to extend without touching routing code.
 */
export const VOICE_FORM_REGISTRY: VoiceFormDefinition[] = [
  {
    id: "file_claim",
    title: "File a claim",
    description: "Live FNOL intake with policy lookup and claim details.",
    intentPatterns: [
      /\b(file\s+a\s+claim|file\s+claim|start\s+a\s+claim|report\s+(?:a\s+)?claim|fnol|first\s+notice\s+of\s+loss|loss\s+report)\b/i,
      /\b(?:i\s+)?(?:want|need|would like)\s+to\s+(?:file|report|start)\s+(?:a\s+)?claim\b/i,
      /\b(?:i'?d|i\s+would)\s+like\s+to\s+file\s+(?:a\s+)?claim\b/i,
      /\b(?:actually|instead|rather)\b.*\b(?:file|report|start)\s+(?:a\s+)?claim\b/i,
    ],
    renderer: "builtin_claim",
    requiresPolicy: false,
  },
  {
    id: "add_vehicle",
    title: "Add vehicle",
    description: "Add a vehicle to your policy using the standard vehicle form.",
    intentPatterns: [
      /\b(add\s+(?:a\s+)?vehicle|add\s+(?:a\s+)?car|new\s+vehicle|new\s+car)\b/i,
      /\b(i\s+)?(?:want|need|would like)\s+to\s+add\s+(?:a\s+)?(?:vehicle|car)\b/i,
      /\b(?:i'?d|i\s+would)\s+like\s+to\s+add\s+(?:a\s+)?(?:vehicle|car)\b/i,
      /\b(?:actually|instead|rather)\b.*\badd\s+(?:a\s+)?(?:vehicle|car)\b/i,
    ],
    renderer: "dynamic",
    requiresPolicy: true,
    loadForm: () => loadAddVehicleFormSchema(),
  },
  {
    id: "add_driver",
    title: "Add driver",
    description: "Add a driver to your policy using the standard driver form.",
    intentPatterns: [
      /\b(add\s+(?:a\s+)?driver|new\s+driver)\b/i,
      /\b(i\s+)?(?:want|need|would like)\s+to\s+add\s+(?:a\s+)?driver\b/i,
      /\b(?:i'?d|i\s+would)\s+like\s+to\s+add\s+(?:a\s+)?driver\b/i,
      /\b(?:actually|instead|rather)\b.*\badd\s+(?:a\s+)?driver\b/i,
    ],
    renderer: "dynamic",
    requiresPolicy: true,
    loadForm: () => loadAddDriverFormSchema(),
  },
];

export function getVoiceFormById(id: VoiceFormIntentId): VoiceFormDefinition | undefined {
  return VOICE_FORM_REGISTRY.find((f) => f.id === id);
}

export function getDynamicVoiceForms(): VoiceFormDefinition[] {
  return VOICE_FORM_REGISTRY.filter((f) => f.renderer === "dynamic");
}
